import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BookOpen, GraduationCap, FileText, Loader2, ArrowUpRight, X, Eye } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, Overline } from "./motion";
import { useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KindIcon = ({ kind, ...props }) =>
  kind === "Course" ? <GraduationCap {...props} /> : <BookOpen {...props} />;

export const Boutique = () => {
  const { c } = useLang();
  const b = c.boutique;
  const [products, setProducts] = useState([]);
  const [buying, setBuying] = useState(null);
  const [preview, setPreview] = useState(null);
  const [invoice, setInvoice] = useState({ invoice_number: "", amount: "" });
  const [payingInvoice, setPayingInvoice] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const buy = async (lookup_key) => {
    setBuying(lookup_key);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || c.contact.toastErr);
      setBuying(null);
    }
  };

  const payInvoice = async (e) => {
    e.preventDefault();
    const amount = parseFloat(invoice.amount);
    if (!invoice.invoice_number.trim() || !amount || amount <= 0) {
      toast.error(b.invoice.number + " + " + b.invoice.amount);
      return;
    }
    setPayingInvoice(true);
    try {
      const { data } = await axios.post(`${API}/payments/invoice-checkout`, {
        invoice_number: invoice.invoice_number.trim(),
        amount,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || c.contact.toastErr);
      setPayingInvoice(false);
    }
  };

  const field =
    "w-full bg-transparent border-b border-[var(--border)] py-3 font-sans text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]/60 focus:border-[var(--accent)] focus:outline-none transition-colors duration-300";

  return (
    <section id="boutique" data-testid="boutique-section" className="py-24 md:py-36 bg-[var(--bg-primary)]">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <Overline>{b.overline}</Overline>
              <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight mt-5 leading-[0.92]">
                {b.headingA}<br /><span className="italic text-[var(--accent)]">{b.headingAccent}</span>
              </h2>
            </div>
            <p className="font-sans text-base md:text-lg text-[var(--text-secondary)] max-w-sm">
              {b.lead}
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mt-14">
          {products.map((p) => (
            <Reveal key={p.lookup_key}>
              <div
                data-testid={`product-${p.lookup_key}`}
                className="group h-full bg-[var(--bg-secondary)] border border-[var(--border)] overflow-hidden hover-lift hover:border-[var(--accent)] flex flex-col"
              >
                {p.cover ? (
                  <div className="relative aspect-[4/5] overflow-hidden bg-[var(--surface)]">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                    />
                    {p.back && (
                      <button
                        data-testid={`preview-${p.lookup_key}`}
                        onClick={() => setPreview(p)}
                        className="absolute bottom-3 right-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--bg-primary)]/80 backdrop-blur-md border border-[var(--border)] text-[var(--text-primary)] text-xs font-mono uppercase tracking-[0.12em] hover:border-[var(--accent)] transition-colors"
                      >
                        <Eye size={13} /> {b.readBack}
                      </button>
                    )}
                  </div>
                ) : null}

                <div className="p-8 md:p-10 flex flex-col justify-between flex-1">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">
                        <KindIcon kind={p.kind} size={16} strokeWidth={1.5} /> {b.kind[p.kind] || p.kind}
                      </span>
                      <span className="font-serif text-2xl text-[var(--text-primary)]">${p.price}</span>
                    </div>
                    <h3 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)] mt-6">{p.title}</h3>
                    {p.subtitle && <p className="font-serif italic text-[var(--accent)] text-lg mt-1">{p.subtitle}</p>}
                    <p className="font-sans text-sm leading-relaxed text-[var(--text-secondary)] mt-3">{p.description}</p>
                  </div>
                  <button
                    data-testid={`buy-${p.lookup_key}`}
                    onClick={() => buy(p.lookup_key)}
                    disabled={buying === p.lookup_key}
                    className="mt-8 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] font-semibold hover:bg-[var(--accent-hover)] transition-colors duration-300 disabled:opacity-60"
                  >
                    {buying === p.lookup_key ? <Loader2 size={16} className="animate-spin" /> : null}
                    {buying === p.lookup_key ? b.redirecting : `${b.buy} — $${p.price}`}
                  </button>
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal>
            <div
              data-testid="invoice-card"
              className="group h-full bg-[var(--surface)] border border-[var(--border)] p-8 md:p-10 flex flex-col justify-between"
            >
              <div>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]">
                  <FileText size={16} strokeWidth={1.5} /> {b.invoice.tag}
                </span>
                <h3 className="font-serif text-2xl md:text-3xl text-[var(--text-primary)] mt-6">{b.invoice.title}</h3>
                <p className="font-sans text-sm leading-relaxed text-[var(--text-secondary)] mt-3">
                  {b.invoice.desc}
                </p>
              </div>
              <form onSubmit={payInvoice} className="mt-6 flex flex-col gap-5">
                <input
                  data-testid="invoice-number"
                  className={field}
                  placeholder={b.invoice.number}
                  value={invoice.invoice_number}
                  onChange={(e) => setInvoice((s) => ({ ...s, invoice_number: e.target.value }))}
                />
                <input
                  data-testid="invoice-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  className={field}
                  placeholder={b.invoice.amount}
                  value={invoice.amount}
                  onChange={(e) => setInvoice((s) => ({ ...s, amount: e.target.value }))}
                />
                <button
                  data-testid="pay-invoice-btn"
                  type="submit"
                  disabled={payingInvoice}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-[var(--accent)] text-[var(--accent)] font-semibold hover:bg-[var(--accent)] hover:text-[var(--bg-primary)] transition-colors duration-300 disabled:opacity-60"
                >
                  {payingInvoice ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                  {payingInvoice ? b.redirecting : b.invoice.pay}
                </button>
              </form>
            </div>
          </Reveal>
        </div>
      </div>

      <AnimatePresence>
        {preview && (
          <motion.div
            data-testid="back-cover-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreview(null)}
            className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[88vh] max-w-[92vw] md:max-w-2xl"
            >
              <button
                data-testid="close-back-cover"
                onClick={() => setPreview(null)}
                className="absolute -top-4 -right-4 z-10 h-10 w-10 rounded-full bg-[var(--accent)] text-[var(--bg-primary)] flex items-center justify-center hover:bg-[var(--accent-hover)] transition-colors"
              >
                <X size={20} />
              </button>
              <img src={preview.back} alt={`${preview.title} — back cover`} className="w-full h-auto max-h-[88vh] object-contain rounded-sm shadow-2xl" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
